import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faEllipsisH,
  faPlus,
  faTimes,
  faSort,
  faCheck,
  faHeart,
  faEye
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../contexts/AuthContext";
import { useSongs } from './songs/SongStateProvider';
import SongFormModal from './songs/SongFormModal';
import SongPartManager from './songs/SongPartManager';
import SongPartPreview from './songs/SongPartPreview';
import { toggleLike, getLikesBatch } from '../services/likeService';
import { useLanguage } from '../contexts/LanguageContext';
import './MobileSongLibrary.css';

const MobileSongLibrary = () => {
  const { t } = useLanguage();
  const { userCommunities } = useAuth();
  const {
    filteredSongs,
    loading,
    searchTerm,
    setSearchTerm,
    selectedOwner,
    handleOwnerChange,
    handleOpenSongModal,
    refreshSongs,
    handleDelete,
    handleSort,
    sortField
  } = useSongs();

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showOptions, setShowOptions] = useState(null);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [showSongModal, setShowSongModal] = useState(false);
  const [showPartModal, setShowPartModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Track liked songs: { [songId]: boolean }
  const [likedSongs, setLikedSongs] = useState({});

  // Fetch like status for visible songs
  const fetchLikeStatuses = useCallback(async () => {
    if (!filteredSongs.length) return;
    const ids = filteredSongs.map(s => s.id);
    try {
      const result = await getLikesBatch('song', ids);
      const newLiked = {};
      for (const [id, data] of Object.entries(result.items)) {
        newLiked[id] = data.liked;
      }
      setLikedSongs(newLiked);
    } catch {
      // ignore
    }
  }, [filteredSongs]);

  useEffect(() => {
    fetchLikeStatuses();
  }, [fetchLikeStatuses]);

  // Define sort options
  const sortOptions = [
    { id: 'title', label: t('songbook.alphabetically'), field: 'title' },
    { id: 'composer', label: t('songbook.composer'), field: 'composer' },
    { id: 'category', label: t('songbook.category'), field: 'category' },
    { id: 'key', label: t('songbook.key'), field: 'key' }
  ];

  // Get current sort option label
  const getCurrentSortLabel = () => {
    const option = sortOptions.find(opt => opt.field === sortField);
    return option ? option.label : t('songbook.sortBy');
  };

  // Toggle search bar
  const toggleSearch = () => {
    setShowSearchBar(!showSearchBar);
    if (showSearchBar) {
      setSearchTerm('');
    }
  };

  // Toggle sort menu
  const toggleSortMenu = () => {
    setShowSortMenu(!showSortMenu);
  };

  // Handle sort selection
  const handleSortSelection = (field) => {
    handleSort(field);
    setShowSortMenu(false);
  };

  // Toggle options
  const toggleOptions = (songId) => {
    setShowOptions(showOptions === songId ? null : songId);
  };

  // Handle open edit song modal
  const handleEditSong = (song) => {
    handleOpenSongModal(song);
    setShowSongModal(true);
    setShowOptions(null);
  };

  // Handle open part manager modal
  const handleOpenPartManager = (song) => {
    setSelectedSong(song);
    setShowPartModal(true);
    setShowOptions(null);
  };

  // Handle open part preview modal
  const handleOpenPartPreview = (song) => {
    setSelectedSong(song);
    setShowPreviewModal(true);
    setShowOptions(null);
  };

  // Handle song created or updated
  const handleSongUpdated = () => {
    refreshSongs();
    setShowSongModal(false);
  };

  // Handle part updated
  const handlePartUpdated = () => {
    refreshSongs();
    setShowPartModal(false);
  };

  // Handle delete song
  const handleDeleteSong = (song) => {
    setSelectedSong(song);
    setShowDeleteConfirm(true);
    setShowOptions(null);
  };

  // Handle like toggle from menu
  const handleToggleLike = async (song) => {
    setShowOptions(null);
    try {
      const result = await toggleLike('song', song.id);
      setLikedSongs(prev => ({ ...prev, [song.id]: result.liked }));
    } catch {
      // ignore
    }
  };

  // Confirm and execute delete
  const confirmDelete = async () => {
    if (selectedSong) {
      await handleDelete(selectedSong.id);
      setShowDeleteConfirm(false);
    }
  };

  // Handle add new song
  const handleAddNewSong = () => {
    handleOpenSongModal(null);
    setShowSongModal(true);
  };

  return (
    <div className="mobile-song-library">
      {/* Header */}
      <div className="mobile-song-header">
        <div className="mobile-song-header-content">
          <h1>{t('songbook.title')}</h1>
          <div className="mobile-song-actions">
            <button className="mobile-header-button" onClick={toggleSearch}>
              <FontAwesomeIcon icon={showSearchBar ? faTimes : faSearch} />
            </button>
            <button className="mobile-header-button" onClick={handleAddNewSong}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        </div>

        {/* Search bar */}
        {showSearchBar && (
          <div className="mobile-search-container">
            <input
              type="text"
              className="mobile-search-input"
              placeholder={t('songbook.searchSongs')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
        )}

        {/* Library tabs */}
        <div className="mobile-song-tabs">
          <button
            className={`mobile-song-tab ${selectedOwner === 'user' ? 'active' : ''}`}
            onClick={() => handleOwnerChange('user')}
          >
            {t('songbook.myLibrary')}
          </button>

          {userCommunities && userCommunities.map(community => (
            <button
              key={community.id}
              className={`mobile-song-tab ${selectedOwner === `community-${community.id}` ? 'active' : ''}`}
              onClick={() => handleOwnerChange(`community-${community.id}`)}
            >
              {community.name || `Skupina ${community.id}`}
            </button>
          ))}
        </div>
      </div>

      {/* Sort button (replaces view toggle) */}
      <div className="mobile-sort-button" onClick={toggleSortMenu}>
        <div className="mobile-sort-label">
          <FontAwesomeIcon icon={faSort} className="mobile-sort-icon" />
          <span>{getCurrentSortLabel()}</span>
        </div>
      </div>

      {/* Song list */}
      <div className="mobile-song-content">
        {loading ? (
          <div className="mobile-song-loading">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('songbook.loading')}</span>
            </div>
          </div>
        ) : filteredSongs.length === 0 ? (
          <div className="mobile-song-empty">
            <p>{t('songbook.noSongs')}</p>
            <button className="btn btn-primary" onClick={handleAddNewSong}>
              <FontAwesomeIcon icon={faPlus} className="me-2" /> {t('songbook.addSong')}
            </button>
          </div>
        ) : (
          <div className="mobile-song-list">
            {filteredSongs.map(song => (
              <div
                className="mobile-song-item"
                key={song.id}
              >
                <div className="mobile-song-item-content">
                  <div className="mobile-song-details" style={{ flex: 1, minWidth: 0 }}>
                    <div className="mobile-song-title">
                      {song.title}
                      {likedSongs[song.id] && (
                        <FontAwesomeIcon
                          icon={faHeart}
                          style={{ color: '#e8a020', marginLeft: '6px', fontSize: '0.75em' }}
                        />
                      )}
                    </div>
                    <div className="mobile-song-meta">
                      {song.composer && <span className="mobile-song-composer">{song.composer}</span>}
                      {song.key && <span className="mobile-song-key">{song.key}</span>}
                    </div>
                    {song.tags && song.tags.length > 0 && (
                      <div className="mobile-song-tags mt-1">
                        {song.tags.map((tag, i) => (
                          <span
                            key={i}
                            style={{
                              fontSize: '0.7em',
                              marginRight: '4px',
                              padding: '1px 6px',
                              border: '1px solid #6c757d',
                              borderRadius: '10px',
                              color: '#6c757d',
                            }}
                          >
                            #{tag.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    className="mobile-song-options"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOptions(song.id);
                    }}
                  >
                    <FontAwesomeIcon icon={faEllipsisH} />
                  </button>
                </div>

                {/* Options menu */}
                {showOptions === song.id && (
                  <div className="mobile-song-options-menu">
                    {/* Like / Unlike */}
                    <button onClick={(e) => {
                      e.stopPropagation();
                      handleToggleLike(song);
                    }}>
                      <FontAwesomeIcon icon={faHeart} className="me-2" style={{ color: likedSongs[song.id] ? '#e8a020' : '#6c757d' }} />
                      {likedSongs[song.id] ? (t('events.likes.unlike') || 'Unlike') : (t('events.likes.like') || 'Like')}
                    </button>
                    {/* Preview notes - only if part file exists */}
                    {song.has_part_file && (
                      <button onClick={(e) => {
                        e.stopPropagation();
                        handleOpenPartPreview(song);
                      }}>
                        <FontAwesomeIcon icon={faEye} className="me-2 text-success" />
                        {t('songbook.table.previewNotes') || 'Preview notes'}
                      </button>
                    )}
                    <button onClick={(e) => {
                      e.stopPropagation();
                      handleEditSong(song);
                    }}>{t('songbook.editSong')}</button>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      handleOpenPartManager(song);
                    }}>{t('songbook.uploadNotes')}</button>
                    <button className="delete-option" onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSong(song);
                    }}>{t('songbook.deleteSong')}</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Floating action button for adding new song */}
      <button
        className="mobile-action-button"
        onClick={handleAddNewSong}
      >
        <FontAwesomeIcon icon={faPlus} />
      </button>

      {/* Sort menu (bottom sheet) */}
      {showSortMenu && (
        <>
          <div className="mobile-sort-menu">
            <div className="mobile-sort-menu-header">
              <h3>{t('songbook.sortBy')}</h3>
            </div>
            <div className="mobile-sort-menu-options">
              {sortOptions.map(option => (
                <button
                  key={option.id}
                  className="mobile-sort-option"
                  onClick={() => handleSortSelection(option.field)}
                >
                  <span>{option.label}</span>
                  {sortField === option.field && (
                    <FontAwesomeIcon icon={faCheck} className="mobile-sort-check" />
                  )}
                </button>
              ))}
            </div>
          </div>
          <div
            className="mobile-backdrop"
            onClick={toggleSortMenu}
          ></div>
        </>
      )}

      {/* Backdrop for options menu */}
      {showOptions && (
        <div
          className="mobile-backdrop"
          onClick={() => setShowOptions(null)}
        ></div>
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <>
          <div className="mobile-delete-modal">
            <div className="mobile-sort-menu-header">
              <h3>{t('songbook.deleteSong')}</h3>
            </div>
            <div className="mobile-delete-confirm">
              <p>{t('songbook.deleteConfirm', { title: selectedSong?.title })}</p>
              <div className="mobile-delete-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  {t('songbook.cancel')}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={confirmDelete}
                >
                  {t('songbook.delete')}
                </button>
              </div>
            </div>
          </div>
          <div
            className="mobile-backdrop"
            onClick={() => setShowDeleteConfirm(false)}
          ></div>
        </>
      )}

      {/* Song Form Modal */}
      <SongFormModal
        show={showSongModal}
        handleClose={() => setShowSongModal(false)}
        onSongCreated={handleSongUpdated}
        onSongUpdated={handleSongUpdated}
      />

      {/* Song Part Manager Modal */}
      <SongPartManager
        show={showPartModal}
        onHide={() => setShowPartModal(false)}
        song={selectedSong}
        onPartUpdated={handlePartUpdated}
      />

      {/* Song Part Preview Modal */}
      <SongPartPreview
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        song={selectedSong}
      />
    </div>
  );
};

export default MobileSongLibrary;
